import { DialogRecepcionFirmada } from "@/erp/inventario/revision_transferencia_inventario/shared/dialog_recepcion_firmada/DialogRecepcionFirmada";
import { Link } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { cellNotFilter, emptyCellObject, formatDateWithTime } from "..";
import { EstadoComponente } from "../components/ui";
import DatePickerFilter from "../components/ui/DatePickerFilter/DatePickerFilter";
import InfoNumeroReferencia from "../components/ui/InfoNumeroReferencia/InfoNumeroReferencia";
import { InfoProductoPorSeries } from "../components/ui/InfoProductoPorSeries/InfoProductoPorSeries";
import InfoUser from "../components/ui/InfoUser/InfoUser";
import {
  EstadoSolicitudTransferencia,
  IDataSolicitudTraslado,
  IDataTrasladoInventario,
  MaterialTransferido,
} from "../interfaces";
import { ITransitoInventario } from "../interfaces/erp/administration/transito_inventario.interface";

const fnColorEstado = (estado: EstadoSolicitudTransferencia) => {
  switch (estado) {
    case EstadoSolicitudTransferencia.EN_ESPERA:
      return "blue";
    case EstadoSolicitudTransferencia.APROBADO:
      return "green";
    case EstadoSolicitudTransferencia.FINALIZADO:
      return "orange";
    case EstadoSolicitudTransferencia.RECHAZADO:
      return "red";
    default:
      return "gray";
  }
};

export const useTableSolicitudTransferenciaInventario = () => {
  const columnaBodegaUbicacion = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        ...cellNotFilter,
        id: "ver_productos",
        accessorKey: "ver_productos",
        header: "Productos/Series",
        Cell: ({ row }) => {
          return (
            <InfoProductoPorSeries
              key={row.original.codigo}
              data={row.original.productos}
            />
          );
        },
      },
      {
        id: "bodega_origen_nombre",
        accessorKey: "bodega_origen_data.nombre",
        header: "Bodega Origen",
        size: 200,
        Cell: ({ row }) =>
          emptyCellObject(row.original.bodega_origen_data, "nombre"),
      },
      {
        id: "ubicacion_origen_nombre",
        accessorKey: "ubicacion_origen_data.nombre",
        header: "Ubicación Origen",
        size: 200,
        Cell: ({ row }) =>
          emptyCellObject(row.original.ubicacion_origen_data, "nombre"),
      },
      {
        id: "bodega_destino_nombre",
        accessorKey: "bodega_destino_data.nombre",
        header: "Bodega destino",
        size: 200,
        Cell: ({ row }) =>
          emptyCellObject(row.original.bodega_destino_data, "nombre"),
      },
      {
        id: "ubicacion_destino_nombre",
        accessorKey: "ubicacion_destino_data.nombre",
        header: "Ubicación Destino",
        size: 200,
        Cell: ({ row }) =>
          emptyCellObject(row.original.ubicacion_destino_data, "nombre"),
      },
    ],
    [],
  );

  //COLUMNA BASE (Solicitud Transferencia de Inventario)
  const columnaBase = useMemo<MRT_ColumnDef<IDataSolicitudTraslado>[]>(
    () => [
      {
        accessorKey: "numero_referencia",
        header: "# referencia",
        size: 200,
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha Solicitud Transferencia",
        size: 200,
        Cell: ({ row }) => (
          <span>{formatDateWithTime(row.original.fecha_creacion!)}</span>
        ),
      },
      {
        accessorKey: "fecha_transferencia",
        header: "Fecha Transferencia",
        size: 200,
        Cell: ({ row }) =>
          row.original.fecha != null
            ? formatDateWithTime(row.original.fecha!)
            : "N/A",
      },
      {
        accessorKey: "estado_solicitud",
        header: "Estado solicitud",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.estado != null ? (
            <EstadoComponente
              backgroundColor={fnColorEstado(
                row.original.estado_solicitud as EstadoSolicitudTransferencia,
              )}
              texto={row.original.estado_solicitud || ""}
            />
          ) : null,
      },
      ...columnaBodegaUbicacion,
      {
        accessorKey: "observaciones",
        header: "Observación",
        size: 200,
        Cell: ({ row }) =>
          row.original.observaciones?.observaciones_1 != null
            ? row.original.observaciones?.observaciones_1
            : "N/A",
      },
      {
        accessorKey: "user.username",
        header: "user",
        size: 200,
      },
    ],
    [],
  );

  //COLUMNA PARA REVISION DE SOLICITUD
  const columnsMovimientoInventario = useMemo<
    MRT_ColumnDef<MaterialTransferido>[]
  >(
    () => [
      {
        accessorKey: "producto_data.codigo",
        header: "Codigo",
        size: 100,
      },
      {
        accessorKey: "producto_data.categoria_producto",
        header: "Categoria",
        size: 150,
      },
      {
        accessorKey: "producto_data.subcategoria_producto",
        header: "SubCategoria",
        size: 150,
        Cell: ({ row }) =>
          row.original.producto_data?.subcategoria_producto ?? "N/A",
      },
      {
        accessorKey: "producto_data.descripcion",
        header: "Descripción",
        size: 150,
      },
      {
        accessorKey: "cantidad",
        header: "Cant. Solicitada",
        maxSize: 150,
      },
    ],
    [],
  );

  const columnaTrasladoInventario = useMemo<
    MRT_ColumnDef<IDataTrasladoInventario>[]
  >(
    () => [
      {
        accessorKey: "numero_referencia",
        header: "# referencia",
        size: 200,
      },
      {
        accessorKey: "movimiento_inventario",
        header: "# Movimiento Kardex",
        size: 150,
        Cell: ({ row }) => {
          return (
            <InfoNumeroReferencia
              movimiento_inventario_data={
                row.original.movimiento_inventario_data
              }
            />
          );
        },
      },
      {
        id: "fecha_creacion",
        accessorKey: "fecha_creacion",
        header: "Fecha Solicitud Transferencia",
        filterFn: "equals",
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_creacion),
        Filter: ({ column }) => {
          const filterValue = column.getFilterValue() as string | undefined;
          return <DatePickerFilter column={column} filterValue={filterValue} />;
        },
      },
      {
        id: "fecha_modificacion",
        accessorKey: "fecha_modificacion",
        header: "Fecha Transferencia",
        filterFn: "equals",
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_modificacion),
        Filter: ({ column }) => {
          const filterValue = column.getFilterValue() as string | undefined;
          return <DatePickerFilter column={column} filterValue={filterValue} />;
        },
      },
      ...columnaBodegaUbicacion,
      {
        accessorKey: "observaciones",
        header: "Observación",
        size: 200,
        Cell: ({ row }) =>
          emptyCellObject(row.original.observaciones, "observaciones_1"),
      },
      {
        id: "solicitado_por",
        accessorKey: "solicitado_por",
        header: "Solicitado por",
        Cell: ({ row }) => emptyCellObject(row.original, "solicitado_por"),
      },
      {
        accessorKey: "user",
        header: "Creado Por",
        size: 200,
        Cell: ({ row }) => <InfoUser user={row.original.user} />,
      },
    ],
    [],
  );

  const columnaTrasladoInventarioEnEspera = useMemo<
    MRT_ColumnDef<IDataTrasladoInventario>[]
  >(
    () => [
      {
        accessorKey: "numero_referencia",
        header: "# referencia",
        size: 200,
      },
      {
        id: "fecha_creacion",
        accessorKey: "fecha_creacion",
        header: "Fecha Solicitud Transferencia",
        filterFn: "equals",
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_creacion),
        Filter: ({ column }) => {
          const filterValue = column.getFilterValue() as string | undefined;
          return <DatePickerFilter column={column} filterValue={filterValue} />;
        },
      },
      {
        id: "fecha_modificacion",
        accessorKey: "fecha_modificacion",
        header: "Fecha Transferencia",
        filterFn: "equals",
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_modificacion),
        Filter: ({ column }) => {
          const filterValue = column.getFilterValue() as string | undefined;
          return <DatePickerFilter column={column} filterValue={filterValue} />;
        },
      },
      ...columnaBodegaUbicacion,
      {
        accessorKey: "observaciones",
        header: "Observación",
        size: 200,
        Cell: ({ row }) =>
          row.original.observaciones
            ? row.original.observaciones.observaciones_1
            : "—",
      },
      {
        accessorKey: "user",
        header: "user",
        size: 200,
        Cell: ({ row }) => <InfoUser user={row.original.user} />,
      },
    ],
    [],
  );

  const columnaRecepcionTrasladoInventario = useMemo<
    MRT_ColumnDef<ITransitoInventario>[]
  >(
    () => [
      {
        accessorKey: "traslado_inicial",
        header: "# referencia",
        size: 200,
      },
      {
        id: "fecha_creacion",
        accessorKey: "fecha_creacion",
        header: "Fecha Solicitud Transferencia",
        filterFn: "equals",
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_creacion),
        Filter: ({ column }) => {
          const filterValue = column.getFilterValue() as string | undefined;
          return <DatePickerFilter column={column} filterValue={filterValue} />;
        },
      },
      {
        id: "fecha_modificacion",
        accessorKey: "fecha_modificacion",
        header: "Fecha Transferencia",
        filterFn: "equals",
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_modificacion),
        Filter: ({ column }) => {
          const filterValue = column.getFilterValue() as string | undefined;
          return <DatePickerFilter column={column} filterValue={filterValue} />;
        },
      },
      {
        accessorKey: "estado_transito",
        header: "estado transito",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.estado != null ? (
            <EstadoComponente
              backgroundColor={"green"}
              texto={row.original.estado_transito}
            />
          ) : null,
      },
      {
        accessorKey: "url_pdf_recibido",
        header: "DOC. RECEPCION",
        size: 100,
        Cell: ({ row }) =>
          row.original.url_pdf_recibido ? (
            <Link
              href={row.original.url_pdf_recibido}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Ver Documento
            </Link>
          ) : row.original.estado_transito === "PENDIENTE DOCUMENTO" ? (
            <DialogRecepcionFirmada dataTransito={row.original} />
          ) : (
            "N/A"
          ),
      },
      ...columnaBodegaUbicacion,
      {
        accessorKey: "observaciones",
        header: "Observación",
        size: 200,
        Cell: ({ row }) =>
          row.original.observaciones
            ? row.original.observaciones.observaciones_1
            : "—",
      },
      {
        accessorKey: "solicitado_por",
        header: "Solicitado por",
        size: 200,
        Cell: ({ row }) =>
          emptyCellObject(row.original.traslado_inicial_data, "solicitado_por"),
      },
      {
        accessorKey: "user",
        header: "Creado por",
        size: 200,
        Cell: ({ row }) => <InfoUser user={row.original.user} />,
      },
    ],
    [],
  );

  return {
    columnaBase,
    columnsMovimientoInventario,
    columnaTrasladoInventario,
    columnaRecepcionTrasladoInventario,
    columnaTrasladoInventarioEnEspera,
  };
};
